import {Button, Dialog, DialogActions} from "@mui/material";
import {useFlag} from "@variocube/app-ui";
import React, {Fragment, useEffect} from "react";
import {useAsync} from "react-async-hook";
import {InvoiceSummary, useInvoicesApi} from "../../api";
import {hasRoles, useAuthContext, useUser} from "../../context/auth";
import {useLocalization} from "../../i18n";
import {canAuditInvoice} from "../../utils/canAuditInvoice";
import {InvoiceAudit, isCarInvoice} from "./details/audit";
import {inSameInbox} from "./details/routing";

interface BulkAuditButtonProps {
	invoices: InvoiceSummary[];
	onAudited: () => any;
}

export function BulkAuditButton({invoices, onAudited}: Readonly<BulkAuditButtonProps>) {
	const {t} = useLocalization();
	const {user} = useAuthContext();

	const [open, setOpen, clearOpen] = useFlag(false);
	const [audited, setAudited, clearAudited] = useFlag(false);

	const visible = hasRoles(user, ["SuperUser", "OebbAdministrator", "OebbUser"]);

	const {loading, result} = useAuditableInvoices(visible, invoices);

	useEffect(() => {
		if (!open && audited) {
			onAudited();
			clearAudited();
		}
	}, [open, audited, onAudited]);

	if (!visible) {
		return null;
	}

	return (
		<Fragment>
			<Button variant="outlined" disabled={loading || result?.length == 0} onClick={setOpen}>
				{t("invoices.details.audit")}
			</Button>
			<Dialog open={open} onClose={clearOpen} fullWidth maxWidth="xs">
				{result && result.length > 0 && <InvoiceAudit invoices={result} onAudited={setAudited} />}
				<DialogActions>
					<Button onClick={clearOpen} color="inherit">{t("actions.close")}</Button>
				</DialogActions>
			</Dialog>
		</Fragment>
	);
}

function useAuditableInvoices(visible: boolean, invoices: InvoiceSummary[]) {
	const {getInvoice} = useInvoicesApi();
	const user = useUser();

	return useAsync(async (visible: boolean, invoices: InvoiceSummary[]) => {
		// don't load anything, if button is not visible
		if (!visible) {
			return [];
		}

		// avoid loading the invoices, if they are not in the same inbox anyway
		if (!inSameInbox(invoices)) {
			return [];
		}

		const loadedInvoices = await Promise.all(invoices.map(invoice => getInvoice(invoice.id)));
		// return empty array if some invoices are not auditable
		if (!loadedInvoices.every(invoice => canAuditInvoice(user, invoice))) {
			return [];
		}

		// car invoices cannot be batch audited, because car data could be different
		if (loadedInvoices.some(isCarInvoice)) {
			return [];
		}

		return loadedInvoices;
	}, [visible, invoices]);
}
